import Vue from 'vue'
import VueRouter from 'vue-router'
import Layout from '@/views/Layout.vue'
import { clearLoginInfo, hasAuthCache, isLoggedIn } from '@/utils/auth'
import { canAccessPath, getDefaultHomePath, MENU_PERMISSION_MAP } from '@/utils/permission'

Vue.use(VueRouter)

function routeMeta(title, path) {
  const permission = MENU_PERMISSION_MAP[path]
  return permission ? { title, permission } : { title }
}

const routes = [
  { path: '/login', name: 'Login', component: () => import('@/views/login/Login.vue'), meta: { public: true, title: '登录' } },
  {
    path: '/',
    component: Layout,
    redirect: () => getDefaultHomePath() || '/login',
    children: [
      { path: 'user', component: () => import('@/views/user/UserList.vue'), meta: routeMeta('用户管理', '/user') },
      { path: 'role', component: () => import('@/views/role/RoleList.vue'), meta: routeMeta('角色管理', '/role') },
      { path: 'permission', component: () => import('@/views/permission/PermissionList.vue'), meta: routeMeta('权限管理', '/permission') },
      { path: 'student', component: () => import('@/views/student/StudentList.vue'), meta: routeMeta('学生管理', '/student') },
      { path: 'campus', component: () => import('@/views/campus/CampusList.vue'), meta: routeMeta('校区管理', '/campus') },
      { path: 'course', component: () => import('@/views/course/CourseList.vue'), meta: routeMeta('课程管理', '/course') },
      { path: 'class', component: () => import('@/views/clazz/ClassList.vue'), meta: routeMeta('班级管理', '/class') },
      { path: 'schedule', component: () => import('@/views/schedule/ScheduleList.vue'), meta: routeMeta('课表管理', '/schedule') },
      { path: 'teacher-schedule', component: () => import('@/views/teacher-schedule/TeacherSchedule.vue'), meta: routeMeta('老师课表', '/teacher-schedule') },
      { path: 'class-duty', component: () => import('@/views/class-duty/ClassDutyList.vue'), meta: routeMeta('老师值班', '/class-duty') },
      { path: 'homework-standard', component: () => import('@/views/homework-standard/HomeworkStandardList.vue'), meta: routeMeta('作业标准管理', '/homework-standard') },
      { path: 'homework', component: () => import('@/views/homework/HomeworkList.vue'), meta: routeMeta('作业管理', '/homework') },
      { path: 'homework-commission', component: () => import('@/views/homework-commission/HomeworkCommissionList.vue'), meta: routeMeta('作业批改', '/homework-commission') },
      { path: 'exam-question', component: () => import('@/views/exam-question/ExamQuestionList.vue'), meta: routeMeta('题库管理', '/exam-question') },
      { path: 'exam-paper', component: () => import('@/views/exam-paper/ExamPaperList.vue'), meta: routeMeta('试卷管理', '/exam-paper') },
      { path: 'exam-test', component: () => import('@/views/exam-test/ExamTestList.vue'), meta: routeMeta('考试管理', '/exam-test') },
      { path: 'exam-review', component: () => import('@/views/exam-review/ExamReviewList.vue'), meta: routeMeta('老师阅卷', '/exam-review') },
      { path: 'product', component: () => import('@/views/product/ProductList.vue'), meta: routeMeta('产品管理', '/product') },
      { path: 'prepayment-order', component: () => import('@/views/prepayment-order/PrepaymentOrderList.vue'), meta: routeMeta('预订单管理', '/prepayment-order') },
      { path: 'order', component: () => import('@/views/order/OrderList.vue'), meta: routeMeta('订单管理', '/order') },
      { path: 'refund-order', component: () => import('@/views/order/RefundOrderList.vue'), meta: routeMeta('退款订单管理', '/refund-order') },
      { path: 'knowledge-base', component: () => import('@/views/knowledge-base/KnowledgeBaseList.vue'), meta: routeMeta('知识库管理', '/knowledge-base') },
      { path: 'knowledge-chunks', component: () => import('@/views/knowledge-base/KnowledgeChunks.vue'), meta: routeMeta('文档分段', '/knowledge-chunks') },
      { path: 'knowledge-recall', component: () => import('@/views/knowledge-base/RecallTest.vue'), meta: routeMeta('召回测试', '/knowledge-recall') },
      { path: 'knowledge-qa', component: () => import('@/views/knowledge-base/KnowledgeQaTest.vue'), meta: routeMeta('知识问答测试', '/knowledge-qa') },
      { path: 'prompt-templates', component: () => import('@/views/prompt-template/PromptTemplateList.vue'), meta: routeMeta('AI 提示词', '/prompt-templates') },
      { path: 'prompt-templates/:templateId/versions', component: () => import('@/views/prompt-template/PromptTemplateVersionList.vue'), meta: { ...routeMeta('提示词版本', '/prompt-templates'), activeMenu: '/prompt-templates' } },
      { path: 'prompt-tests', component: () => import('@/views/prompt-template/PromptTest.vue'), meta: routeMeta('提示词测试', '/prompt-tests') },
      { path: 'text-to-sql-analysis', component: () => import('@/views/text-to-sql/TextToSqlAnalysis.vue'), meta: routeMeta('数据分析', '/text-to-sql-analysis') },
      { path: 'text-to-sql-runs', component: () => import('@/views/text-to-sql/TextToSqlRunRecords.vue'), meta: routeMeta('运行记录', '/text-to-sql-runs') },
      { path: 'text-to-sql-permissions', component: () => import('@/views/text-to-sql/TextToSqlPermissions.vue'), meta: routeMeta('Text-to-SQL 权限', '/text-to-sql-permissions') },
      { path: 'text-to-sql-eval-samples', component: () => import('@/views/text-to-sql/TextToSqlEvalSamples.vue'), meta: routeMeta('评测样本', '/text-to-sql-eval-samples') },
      { path: 'text-to-sql-eval-tasks', component: () => import('@/views/text-to-sql/TextToSqlEvalTasks.vue'), meta: routeMeta('评测任务', '/text-to-sql-eval-tasks') },
      { path: 'config', component: () => import('@/views/config/SysConfig.vue'), meta: routeMeta('参数管理', '/config') },
      { path: 'class/:classId/schedule', redirect: to => ({ path: '/schedule', query: { classId: to.params.classId } }) }
    ]
  }
]

const router = new VueRouter({ mode: 'history', routes })

router.beforeEach((to, from, next) => {
  document.title = to.meta.title ? `${to.meta.title} - YanQue Admin` : 'YanQue Admin'

  if (to.meta.public) {
    if (to.path === '/login' && isLoggedIn()) {
      // 旧会话没有角色/权限缓存，强制重新登录
      if (!hasAuthCache()) {
        clearLoginInfo()
        next()
        return
      }
      const home = getDefaultHomePath()
      next(home || '/')
      return
    }
    next()
    return
  }

  if (!isLoggedIn()) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  // 权限改造前的 token 没有 roles/permissions，直接清掉并回登录页
  if (!hasAuthCache()) {
    clearLoginInfo()
    next({ path: '/login', query: { redirect: to.fullPath, reason: 'relogin' } })
    return
  }

  // 路由级权限：无权限则跳到首个可访问页；都没有则回登录
  if (to.meta.permission && !canAccessPath(to.path)) {
    const home = getDefaultHomePath()
    if (home && home !== to.path) {
      next(home)
      return
    }
    next({ path: '/login', query: { reason: 'no-permission' } })
    return
  }

  next()
})

export default router

